import type { AccountLinkingCode } from "@sidegigllc/api/client";
import { safeSessionStorage } from "utils/safeStorage";

// Both survive the sign-out and hard navigation the linking flow goes through: the code between
// minting and spending it, the opt-out because redirects out of sign-up drop the search param.
const CODE_KEY = "qwick.accountLinkingCode";
const SKIPPED_KEY = "qwick.skipAccountLinking";

type StashedCode = {
  code: string;
  /** Epoch ms — a `Date` does not survive `JSON.stringify`. */
  expiresTs: number;
};

const isStashedCode = (value: unknown): value is StashedCode =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as StashedCode).code === "string" &&
  typeof (value as StashedCode).expiresTs === "number";

/** False when storage rejected the code — the caller must not sign out. */
export function stashAccountLinkingCode({ code, expires_ts }: AccountLinkingCode): boolean {
  const stashed: StashedCode = { code, expiresTs: new Date(expires_ts).getTime() };
  return safeSessionStorage.setItem(CODE_KEY, JSON.stringify(stashed));
}

export function clearAccountLinkingCode() {
  safeSessionStorage.removeItem(CODE_KEY);
}

export function readAccountLinkingCode(): string | undefined {
  const stored = safeSessionStorage.getItem(CODE_KEY);
  if (!stored) return;

  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    clearAccountLinkingCode();
    return;
  }
  if (!isStashedCode(parsed)) {
    clearAccountLinkingCode();
    return;
  }
  if (Date.now() >= parsed.expiresTs) {
    clearAccountLinkingCode();
    return;
  }
  return parsed.code;
}

export function markAccountLinkingSkipped() {
  safeSessionStorage.setItem(SKIPPED_KEY, "true");
}

export function isAccountLinkingSkipped() {
  return safeSessionStorage.getItem(SKIPPED_KEY) === "true";
}

export function clearAccountLinkingSkipped() {
  safeSessionStorage.removeItem(SKIPPED_KEY);
}
